import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Users, Loader2, Eye, EyeOff, CheckCircle2, Clock, DollarSign, History, Receipt } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { PaymentHistoryModal } from './PaymentHistoryModal';

// Modal para mostrar participantes de um item do roteiro
export function ParticipantsModal({ tripId, itemId, itemTitle, isOrganizer, trigger }) {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchParticipants = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/trips/${tripId}/itinerary/${itemId}/participants`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching participants:', error);
      toast.error('Erro ao carregar participantes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchParticipants();
    }
  }, [open]);

  const handleToggleTransparency = async () => {
    if (!data) return;
    
    try {
      await api.put(`/trips/${tripId}/itinerary/${itemId}/transparency`, {
        is_transparent: !data.is_transparent
      });
      setData({ ...data, is_transparent: !data.is_transparent });
      toast.success(`Visibilidade ${!data.is_transparent ? 'ativada' : 'desativada'}`);
    } catch (error) {
      toast.error('Erro ao atualizar configuração');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700 dark:text-slate-300">
            <Users className="w-4 h-4 mr-1" />
            Ver participantes
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-500" />
            Participantes
          </DialogTitle>
          <p className="text-sm text-slate-500 dark:text-slate-400">{itemTitle}</p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : data ? (
          <div className="space-y-4 mt-4">
            {/* Organizer controls */}
            {isOrganizer && (
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-2">
                  {data.is_transparent ? (
                    <Eye className="w-4 h-4 text-green-500" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-slate-400" />
                  )}
                  <Label htmlFor="transparency" className="text-sm">
                    Visível para todos
                  </Label>
                </div>
                <Switch
                  id="transparency"
                  checked={data.is_transparent}
                  onCheckedChange={handleToggleTransparency}
                />
              </div>
            )}

            {/* Message if can't view */}
            {!data.can_view && (
              <div className="text-center py-4 text-slate-500 dark:text-slate-400">
                <EyeOff className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Lista de participantes não está visível</p>
                <p className="text-xs">O organizador desativou esta visualização</p>
              </div>
            )}

            {/* Participants list */}
            {data.can_view && (
              <>
                <div className="flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
                  <span>{data.is_for_everyone ? 'Todos os membros' : 'Membros selecionados'}</span>
                  <Badge variant="outline">{data.total_count} participantes</Badge>
                </div>

                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {data.participants.map((participant) => (
                    <div
                      key={participant.user_id}
                      className="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700"
                    >
                      <Avatar className="w-10 h-10">
                        <AvatarFallback className="bg-orange-100 dark:bg-orange-900/30 text-lg">
                          {participant.avatar || '😊'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 dark:text-white">
                          {participant.name}
                        </p>
                      </div>
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// Modal para mostrar status de pagamento de cada membro
export function PaymentStatusModal({ tripId, paymentId, paymentTitle, totalAmount, isOrganizer, trigger }) {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/trips/${tripId}/payments/${paymentId}/status`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching payment status:', error);
      toast.error('Erro ao carregar status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchStatus();
    }
  }, [open]);

  const handleToggleTransparency = async () => {
    if (!data) return;
    
    try {
      await api.put(`/trips/${tripId}/payments/${paymentId}/transparency`, {
        is_transparent: !data.is_transparent
      });
      setData({ ...data, is_transparent: !data.is_transparent });
      toast.success(`Visibilidade ${!data.is_transparent ? 'ativada' : 'desativada'}`);
    } catch (error) {
      toast.error('Erro ao atualizar configuração');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700 dark:text-slate-300">
            <Users className="w-4 h-4 mr-1" />
            Ver status
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-500" />
            Status do Pagamento
          </DialogTitle>
          <p className="text-sm text-slate-500 dark:text-slate-400">{paymentTitle}</p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : data ? (
          <div className="space-y-4 mt-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-lg font-bold text-green-600">{data.paid_count}</p>
                <p className="text-xs text-green-600">Pagaram</p>
              </div>
              <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <p className="text-lg font-bold text-orange-600">{data.total_count - data.paid_count}</p>
                <p className="text-xs text-orange-600">Pendentes</p>
              </div>
              <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-lg font-bold text-slate-700 dark:text-slate-300">{data.total_count}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Total</p>
              </div>
            </div>

            {/* Organizer controls */}
            {isOrganizer && (
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-2">
                  {data.is_transparent ? (
                    <Eye className="w-4 h-4 text-green-500" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-slate-400" />
                  )}
                  <Label htmlFor="payment-transparency" className="text-sm">
                    Visível para todos
                  </Label>
                </div>
                <Switch
                  id="payment-transparency"
                  checked={data.is_transparent}
                  onCheckedChange={handleToggleTransparency}
                />
              </div>
            )}

            {/* Message if can't view */}
            {!data.can_view && (
              <div className="text-center py-4 text-slate-500 dark:text-slate-400">
                <EyeOff className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Status detalhado não está visível</p>
                <p className="text-xs">O organizador desativou esta visualização</p>
              </div>
            )}

            {/* Members list */}
            {data.can_view && (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {data.members.map((member) => (
                  <div
                    key={member.user_id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      member.is_paid 
                        ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800' 
                        : 'bg-orange-50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-800'
                    }`}
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className={`text-lg ${
                        member.is_paid 
                          ? 'bg-green-100 dark:bg-green-900/30' 
                          : 'bg-orange-100 dark:bg-orange-900/30'
                      }`}>
                        {member.avatar || '😊'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-white truncate">
                        {member.name}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <span>{formatCurrency(member.amount_paid)} / {formatCurrency(member.amount_due)}</span>
                        {member.is_paid && member.paid_at && (
                          <span className="text-green-600">
                            • {format(new Date(member.paid_at), "dd/MM", { locale: ptBR })}
                          </span>
                        )}
                        {member.proof_url && (
                          <span className="text-blue-600 flex items-center gap-0.5">
                            <Receipt className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {isOrganizer && (
                        <PaymentHistoryModal
                          tripId={tripId}
                          memberId={member.user_id}
                          memberName={member.name}
                          memberAvatar={member.avatar}
                          isOrganizer={isOrganizer}
                          trigger={
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0"
                              title="Ver histórico"
                            >
                              <History className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                            </Button>
                          }
                        />
                      )}
                      {member.is_paid ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <Clock className="w-5 h-5 text-orange-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Totals */}
            {data.can_view && (
              <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500 dark:text-slate-400">Total arrecadado:</span>
                  <span className="font-medium text-green-600">{formatCurrency(data.total_paid)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-slate-500 dark:text-slate-400">Pendente:</span>
                  <span className="font-medium text-orange-600">{formatCurrency(data.total_pending)}</span>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default { ParticipantsModal, PaymentStatusModal };
